import type { ComponentProps } from 'react';
import React, { useEffect } from 'react';
import { useEffectEvent } from 'use-effect-event';
import * as styles from './PostModalView.module.scss';
import PostModalViewMoveable from './PostModalViewMoveable';
import PostModalViewResizeButton from './PostModalViewResizeButton';
import Button from '../../components/Button';
import ScrollArea from '../../components/ScrollArea';
import SVGIcon from '../../components/SVGIcon';
import { AssetTokenProvider } from '../../editor/AssetTokenContext';
import QuillComponent from '../../editor/QuillComponent';
import { useQuill, withQuillContextProvider } from '../../editor/QuillContext';
import useHeaderCollapse from '../../editor/useHeaderCollapse';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import clearIcon from '../../icons/non-prefilled/16x16/clear.svg';
import collapseIcon from '../../icons/non-prefilled/16x16/collapse.svg';
import expandIcon from '../../icons/non-prefilled/16x16/expand.svg';
import newTabIcon from '../../icons/non-prefilled/16x16/newTab.svg';
import { LinkForceNewTabContext } from '../../utils/router/Link';

const POST_FRAGMENT = gql(/** @complexity 23 */ `
  fragment PostModalView_post on Post {
    id
    content {
      id
      delta
    }
    banner {
      original
    }
  }
`);

const ENTITY_ASSET_TOKEN_FRAGMENT = gql(/** @complexity 1 */ `
  fragment PostModalView_entityAssetToken on EntityAssetToken {
    ...AssetTokenProvider_entityAssetToken
  }
`);

interface PostModalViewProps
  extends ComponentProps<typeof PostModalViewResizeButton>,
    ComponentProps<typeof PostModalViewMoveable> {
  post?: DocumentType<typeof POST_FRAGMENT>;
  entityAssetToken?: DocumentType<typeof ENTITY_ASSET_TOKEN_FRAGMENT>;
  headerBar: React.ReactNode;
  magneticWall?: React.ReactNode;
  newTabTo?: string;
  frameless?: boolean;
  expanded?: boolean;
  onExpandChange?: (expanded: boolean) => void;
  onClose: () => void;
}

const PostModalView = withQuillContextProvider(
  ({
    post,
    entityAssetToken,
    headerBar,
    magneticWall,
    newTabTo,
    frameless,
    expanded,
    onExpandChange,
    onClose,
    onDraggingStart,
    onDraggingMove,
    onDraggingEnd,
    onResizeStart,
    onResizeMove,
    onResizeEnd,
  }: PostModalViewProps) => {
    const { quill } = useQuill();
    useHeaderCollapse(quill);

    const shouldHandleEscape = expanded && !!onExpandChange;
    const onEscapePressed = useEffectEvent(() => onExpandChange?.(false));
    useEffect(() => {
      if (!shouldHandleEscape) return undefined;

      const listener = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          onEscapePressed();
        }
      };

      document.body.addEventListener('keydown', listener);
      return () => {
        document.body.removeEventListener('keydown', listener);
      };
    }, [shouldHandleEscape]);

    const moveableProps = onDraggingStart
      ? { onDraggingStart, onDraggingMove, onDraggingEnd }
      : {};

    const content = post && entityAssetToken && (
      <LinkForceNewTabContext.Provider value>
        <div className={styles.container}>
          <ScrollArea className={styles.scrollArea}>
            <PostModalViewMoveable
              className={styles.contentContainer}
              {...moveableProps}
            >
              {post.banner.original ? (
                <div className={styles.bannerContainer}>
                  <img
                    className={styles.banner}
                    draggable={false}
                    src={post.banner.original}
                    alt=""
                  />
                </div>
              ) : null}
              <div className={styles.headerBarContainer}>{headerBar}</div>
              <div className={styles.content}>
                <div className={styles.editorContainer}>
                  <AssetTokenProvider entityAssetToken={entityAssetToken}>
                    <QuillComponent
                      initialContent={post.content.delta}
                      rootClassName={styles.editorRoot}
                      contentPlaceholder=""
                      namespace="extensionPostCard"
                      readOnly
                      titlePlaceholder="Untitled"
                    />
                  </AssetTokenProvider>
                </div>
              </div>
              <div className={styles.padding} />
              <div className={styles.magneticWallContainer}>{magneticWall}</div>
            </PostModalViewMoveable>
          </ScrollArea>
          <PostModalViewResizeButton
            onResizeStart={onResizeStart}
            onResizeMove={onResizeMove}
            onResizeEnd={onResizeEnd}
          />
          <div className={styles.buttonGroup}>
            <Button
              size="small"
              className={styles.button}
              onClick={() => onClose()}
            >
              <SVGIcon size={14}>{clearIcon}</SVGIcon>
            </Button>
            {onExpandChange && (
              <Button
                size="small"
                className={styles.button}
                onClick={() => {
                  onExpandChange(!expanded);
                }}
              >
                <SVGIcon size={14}>
                  {expanded ? collapseIcon : expandIcon}
                </SVGIcon>
              </Button>
            )}
            {!!newTabTo && (
              <Button size="small" to={newTabTo} className={styles.button}>
                <SVGIcon size={14}>{newTabIcon}</SVGIcon>
              </Button>
            )}
          </div>
        </div>
      </LinkForceNewTabContext.Provider>
    );

    return frameless ? content : <div className={styles.modal}>{content}</div>;
  },
);

export default PostModalView;
